import { useState, useEffect, useMemo, Fragment } from "react";
import {
  useStaffCommunicationList,
  useStaffCommunicationStats,
  useRetryStaffCommunication,
} from "@/hooks/useStaffCommunications";
import type {
  StaffCommunicationMedium,
  StaffCommunicationStatus,
  StaffCommunicationProvider,
  StaffCommunicationListParams,
  StaffCommunicationRecord,
} from "@/lib/staff-communications-api";
import { staffAPI } from "@/lib/staff-api";
import type { StaffMember } from "@/lib/staff-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Search,
  Loader2,
  MessageCircle,
  Mail,
  Smartphone,
  Phone,
  Bell,
  Globe,
  Radio,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  BarChart3,
  AlertCircle,
  Hash,
  RotateCw,
} from "lucide-react";
import { cn } from "@/lib/utils";

const MEDIUM_ICONS: Record<StaffCommunicationMedium, React.ElementType> = {
  whatsapp: MessageCircle,
  email: Mail,
  sms: Smartphone,
  phone: Phone,
  push_notification: Bell,
  web_push_notification: Globe,
  slack: Hash,
};

const MEDIUM_LABELS: Record<StaffCommunicationMedium, string> = {
  whatsapp: "WhatsApp",
  email: "Email",
  sms: "SMS",
  phone: "Phone",
  push_notification: "Push",
  web_push_notification: "Web Push",
  slack: "Slack",
};

const STATUS_VARIANTS: Record<
  StaffCommunicationStatus,
  "success" | "critical" | "warning" | "neutral"
> = {
  sent: "success",
  failed: "critical",
  pending: "warning",
  delivered: "neutral",
  bounced: "warning",
};

const STATUS_COLORS: Record<StaffCommunicationStatus, string> = {
  sent: "text-success bg-success/10 border-success/30",
  failed: "text-destructive bg-destructive/10 border-destructive/30",
  pending: "text-warning bg-warning/10 border-warning/30",
  // theme-drift: decorative
  delivered: "text-blue-600 bg-blue-50 border-blue-200",
  bounced: "text-warning bg-warning border-warning/30",
};

const MEDIUM_OPTIONS: StaffCommunicationMedium[] = [
  "whatsapp",
  "email",
  "sms",
  "phone",
  "push_notification",
  "web_push_notification",
  "slack",
];

const STATUS_OPTIONS: StaffCommunicationStatus[] = [
  "pending",
  "sent",
  "failed",
  "delivered",
  "bounced",
];

const PROVIDER_OPTIONS: {
  value: StaffCommunicationProvider;
  label: string;
}[] = [
  { value: "onesignal", label: "OneSignal" },
  { value: "msg91", label: "MSG91" },
  { value: "superfone", label: "Superfone" },
  { value: "aisensy", label: "AiSensy" },
  { value: "slack", label: "Slack" },
];

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const StaffCommunicationLogs = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [staffResults, setStaffResults] = useState<StaffMember[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Retry confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [commToRetry, setCommToRetry] =
    useState<StaffCommunicationRecord | null>(null);

  const [filterMedium, setFilterMedium] = useState<
    StaffCommunicationMedium | ""
  >("");
  const [filterStatus, setFilterStatus] = useState<
    StaffCommunicationStatus | ""
  >("");
  const [filterProvider, setFilterProvider] = useState<
    StaffCommunicationProvider | ""
  >("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  const listParams = useMemo(
    (): StaffCommunicationListParams => ({
      console_user_id: selectedStaff?.id?.toString() || "",
      ...(filterMedium && { medium: filterMedium }),
      ...(filterStatus && { status: filterStatus }),
      ...(filterProvider && { provider: filterProvider }),
      ...(filterStartDate && { start_date: filterStartDate }),
      ...(filterEndDate && { end_date: filterEndDate }),
      page: currentPage,
      page_size: pageSize,
    }),
    [
      selectedStaff,
      filterMedium,
      filterStatus,
      filterProvider,
      filterStartDate,
      filterEndDate,
      currentPage,
      pageSize,
    ]
  );

  const {
    data: commsData,
    isLoading: isLoadingComms,
    error: commsError,
  } = useStaffCommunicationList(listParams);

  const { data: statsData, isLoading: isLoadingStats } =
    useStaffCommunicationStats(selectedStaff?.id?.toString() || "");

  const retryMutation = useRetryStaffCommunication();

  // Debounced staff search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setStaffResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await staffAPI.listStaff({ search: searchQuery });
        setStaffResults(result.results || []);
      } catch {
        setStaffResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    filterMedium,
    filterStatus,
    filterProvider,
    filterStartDate,
    filterEndDate,
  ]);

  const totalPages = commsData ? Math.ceil(commsData.count / pageSize) : 0;

  const clearFilters = () => {
    setFilterMedium("");
    setFilterStatus("");
    setFilterProvider("");
    setFilterStartDate("");
    setFilterEndDate("");
    setCurrentPage(1);
  };

  const hasActiveFilters =
    filterMedium ||
    filterStatus ||
    filterProvider ||
    filterStartDate ||
    filterEndDate;

  const handleSelectStaff = (staff: StaffMember) => {
    setSelectedStaff(staff);
    setSearchQuery("");
    setStaffResults([]);
    setExpandedRow(null);
    clearFilters();
  };

  const handleChangeStaff = () => {
    setSelectedStaff(null);
    setSearchQuery("");
    setStaffResults([]);
    setExpandedRow(null);
    clearFilters();
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Staff search or selected staff info */}
      <div className="flex items-center justify-between gap-3">
        {!selectedStaff ? (
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
            <Input
              placeholder="Search staff by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        ) : (
          <div className="flex flex-1 items-center gap-3 p-3 bg-muted/30 rounded-xl border border-border">
            <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center text-xs font-medium text-foreground">
              {selectedStaff.first_name?.[0] || ""}
              {selectedStaff.last_name?.[0] || ""}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {selectedStaff.first_name} {selectedStaff.last_name}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {selectedStaff.user_type__name}
              </p>
            </div>
          </div>
        )}
        {selectedStaff && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleChangeStaff}
            className="gap-1.5 shrink-0"
          >
            <X className="h-3.5 w-3.5" />
            Change Staff
          </Button>
        )}
      </div>

      {/* Search results dropdown */}
      {!selectedStaff && searchQuery.trim() && (
        <div className="border border-border rounded-xl max-h-60 overflow-auto">
          {isSearching ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">
                Searching...
              </span>
            </div>
          ) : staffResults.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No staff found
            </div>
          ) : (
            staffResults.map((staff) => (
              <button
                key={staff.id}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/30 border-b last:border-b-0 text-left transition-colors"
                onClick={() => handleSelectStaff(staff)}
              >
                <div className="w-7 h-7 bg-muted/80 rounded-full flex items-center justify-center text-xs font-medium text-muted-foreground shrink-0">
                  {staff.first_name?.[0] || ""}
                  {staff.last_name?.[0] || ""}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {staff.first_name} {staff.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {staff.user_type__name}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Empty state — no staff selected, no search */}
      {!selectedStaff && !searchQuery.trim() && (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <div className="p-6 bg-muted rounded-2xl mb-4">
            <Radio className="h-12 w-12 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium">
            Search for a staff member to view their communication history
          </p>
        </div>
      )}

      {/* Selected staff content */}
      {selectedStaff && (
        <>
          {/* Stats summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 shrink-0">
            {isLoadingStats ? (
              <>
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="border border-border rounded-xl p-4 animate-pulse bg-muted/30 h-24"
                  />
                ))}
              </>
            ) : statsData ? (
              <>
                {/* Total */}
                <div className="relative rounded-xl p-4 bg-card border border-border overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 bg-muted rounded-lg">
                      <BarChart3 className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                      Total
                    </span>
                  </div>
                  <p className="text-3xl font-bold text-foreground tabular-nums">
                    {statsData.total}
                  </p>
                </div>

                {/* By Medium */}
                <div className="relative rounded-xl p-4 bg-card border border-border overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
                  <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    By Medium
                  </span>
                  <div className="mt-2 space-y-1">
                    {statsData.by_medium.map((item) => {
                      const Icon =
                        MEDIUM_ICONS[item.communication_medium] || Radio;
                      return (
                        <div
                          key={item.communication_medium}
                          className="flex items-center justify-between text-sm"
                        >
                          <div className="flex items-center gap-1.5">
                            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-foreground">
                              {MEDIUM_LABELS[item.communication_medium] ||
                                item.communication_medium}
                            </span>
                          </div>
                          <span className="font-medium text-foreground">
                            {item.count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* By Status */}
                <div className="relative rounded-xl p-4 bg-card border border-border overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
                  <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    By Status
                  </span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {statsData.by_status.map((item) => (
                      <Badge
                        key={item.status}
                        variant={STATUS_VARIANTS[item.status]}
                        className="text-xs capitalize"
                      >
                        {item.status} ({item.count})
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            ) : null}
          </div>

          {/* Filters row */}
          <div className="flex flex-wrap items-center gap-4 shrink-0">
            <Select
              value={filterMedium || "all"}
              onValueChange={(v) =>
                setFilterMedium(
                  v === "all" ? "" : (v as StaffCommunicationMedium)
                )
              }
            >
              <SelectTrigger className="w-[140px] h-8 text-xs">
                <SelectValue placeholder="All Mediums" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mediums</SelectItem>
                {MEDIUM_OPTIONS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {MEDIUM_LABELS[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filterStatus || "all"}
              onValueChange={(v) =>
                setFilterStatus(
                  v === "all" ? "" : (v as StaffCommunicationStatus)
                )
              }
            >
              <SelectTrigger className="w-[140px] h-8 text-xs">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filterProvider || "all"}
              onValueChange={(v) =>
                setFilterProvider(
                  v === "all" ? "" : (v as StaffCommunicationProvider)
                )
              }
            >
              <SelectTrigger className="w-[140px] h-8 text-xs">
                <SelectValue placeholder="All Providers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Providers</SelectItem>
                {PROVIDER_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="w-[140px] h-8 text-xs"
              placeholder="Start date"
            />
            <Input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="w-[140px] h-8 text-xs"
              placeholder="End date"
            />

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-8 text-xs gap-1 text-muted-foreground"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </Button>
            )}
          </div>

          {/* Communications table */}
          <div className="min-h-0 overflow-auto border border-border rounded-xl">
            {isLoadingComms ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-3 text-sm text-muted-foreground">
                  Loading communications...
                </span>
              </div>
            ) : commsError ? (
              <div className="flex flex-col items-center justify-center py-16 text-destructive">
                <div className="p-4 bg-destructive/10 rounded-full mb-4">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                </div>
                <p className="text-sm font-medium">
                  {commsError instanceof Error
                    ? commsError.message
                    : "Failed to load communications"}
                </p>
              </div>
            ) : !commsData || commsData.results.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <div className="p-4 bg-muted rounded-full mb-4">
                  <Radio className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium">No communications found</p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Try adjusting your filters
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="w-[50px]">Medium</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Template / Content</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-[40px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commsData.results.map((comm: StaffCommunicationRecord) => {
                    const MediumIcon =
                      MEDIUM_ICONS[comm.communication_medium] || Radio;
                    const isExpanded = expandedRow === comm.id;
                    return (
                      <Fragment key={comm.id}>
                        <TableRow
                          className="cursor-pointer hover:bg-muted/30 transition-colors border-border"
                          onClick={() =>
                            setExpandedRow(isExpanded ? null : comm.id)
                          }
                        >
                          <TableCell>
                            <MediumIcon className="h-4 w-4 text-muted-foreground" />
                          </TableCell>
                          <TableCell className="text-sm text-foreground capitalize">
                            {comm.communication_provider}
                          </TableCell>
                          <TableCell className="text-sm text-foreground max-w-[200px] truncate">
                            {comm.template_name || comm.template_id || "\u2014"}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground font-mono text-xs">
                            {comm.recipient}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className={cn(
                                  "text-xs capitalize",
                                  STATUS_COLORS[comm.status]
                                )}
                              >
                                {comm.status}
                              </Badge>
                              {comm.retry_count > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  {comm.retry_count}x retries
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {formatDate(comm.created_at)}
                          </TableCell>
                          <TableCell>
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow className="border-border">
                            <TableCell
                              colSpan={7}
                              className="bg-muted/30 px-6 py-4"
                            >
                              <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                <div>
                                  <span className="font-medium text-muted-foreground">
                                    External ID:
                                  </span>{" "}
                                  <span className="text-foreground font-mono text-xs">
                                    {comm.external_id || "\u2014"}
                                  </span>
                                </div>
                                <div>
                                  <span className="font-medium text-muted-foreground">
                                    Template ID:
                                  </span>{" "}
                                  <span className="text-foreground font-mono text-xs">
                                    {comm.template_id || "\u2014"}
                                  </span>
                                </div>
                                {comm.error_code && (
                                  <div>
                                    <span className="font-medium text-muted-foreground">
                                      Error Code:
                                    </span>{" "}
                                    <span className="text-destructive font-mono text-xs">
                                      {comm.error_code}
                                    </span>
                                  </div>
                                )}
                                {comm.error_message && (
                                  <div className="col-span-2">
                                    <span className="font-medium text-muted-foreground">
                                      Error Message:
                                    </span>{" "}
                                    <span className="text-destructive text-xs">
                                      {comm.error_message}
                                    </span>
                                  </div>
                                )}
                                {comm.metadata &&
                                  Object.keys(comm.metadata).length > 0 && (
                                    <div className="col-span-2">
                                      <span className="font-medium text-muted-foreground">
                                        Metadata:
                                      </span>
                                      <pre className="mt-2 text-xs text-muted-foreground bg-muted p-3 rounded-lg border border-border overflow-auto max-h-32">
                                        {JSON.stringify(comm.metadata, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                {/* New Phase 3 fields */}
                                {comm.http_status_code && (
                                  <div>
                                    <span className="font-medium text-muted-foreground">
                                      HTTP Status:
                                    </span>{" "}
                                    <span
                                      className={`font-mono text-xs ${
                                        comm.http_status_code >= 200 &&
                                        comm.http_status_code < 300
                                          ? "text-success"
                                          : "text-destructive"
                                      }`}
                                    >
                                      {comm.http_status_code}
                                    </span>
                                  </div>
                                )}
                                {comm.response_time_ms && (
                                  <div>
                                    <span className="font-medium text-muted-foreground">
                                      Response Time:
                                    </span>{" "}
                                    <span className="text-foreground font-mono text-xs">
                                      {comm.response_time_ms}ms
                                    </span>
                                  </div>
                                )}
                                {comm.provider_response &&
                                  Object.keys(comm.provider_response).length >
                                    0 && (
                                    <div className="col-span-2">
                                      <span className="font-medium text-muted-foreground">
                                        Provider Response:
                                      </span>
                                      <pre className="mt-2 text-xs text-muted-foreground bg-muted p-3 rounded-lg border border-border overflow-auto max-h-48">
                                        {JSON.stringify(
                                          comm.provider_response,
                                          null,
                                          2
                                        )}
                                      </pre>
                                    </div>
                                  )}
                                {comm.parent_communication && (
                                  <div className="col-span-2">
                                    <span className="font-medium text-muted-foreground">
                                      Retry of:
                                    </span>{" "}
                                    <span className="text-foreground font-mono text-xs">
                                      {comm.parent_communication}
                                    </span>
                                  </div>
                                )}
                                {/* Retry button for all communications */}
                                <div className="col-span-2 pt-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Show confirmation for successful communications
                                      if (
                                        comm.status === "sent" ||
                                        comm.status === "delivered"
                                      ) {
                                        setCommToRetry(comm);
                                        setConfirmDialogOpen(true);
                                      } else {
                                        // Direct retry for failed/pending/bounced
                                        retryMutation.mutate(comm.id);
                                      }
                                    }}
                                    disabled={retryMutation.isPending}
                                  >
                                    {retryMutation.isPending ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Retrying...
                                      </>
                                    ) : (
                                      <>
                                        <RotateCw className="mr-2 h-4 w-4" />
                                        Retry Communication
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Pagination */}
          {commsData && commsData.count > 0 && (
            <div className="flex items-center justify-between pt-1 shrink-0">
              <p className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * pageSize + 1}&ndash;
                {Math.min(currentPage * pageSize, commsData.count)} of{" "}
                {commsData.count} results
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Retry Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Retry</DialogTitle>
            <DialogDescription>
              This communication was already {commToRetry?.status}. Are you sure
              you want to retry it?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (commToRetry) {
                  retryMutation.mutate(commToRetry.id);
                }
                setConfirmDialogOpen(false);
                setCommToRetry(null);
              }}
            >
              Retry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
